import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, Eye, Lock, MessageCircle } from 'lucide-react';
import { withNumber } from '../../utils/participantNumber';

/**
 * One player's (or observer's) view of a micro simulation.
 *
 * Renders exactly what the server sent — `state` comes from the redacting serializer
 * (backend utils/microSimSerializer.js): your own private brief, the conversation so
 * far, and options only on your turn. Nothing here decides turns or scores.
 *
 * Shared by the participant's phone (MicroSimPlayPage) and the trainer's "preview as a
 * pair" (MicroSimPreviewPage).
 */

/** Seconds until an ISO time, never negative. */
const secondsUntil = (iso) => (iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 1000)) : 0);

const Bubble = ({ line }) => (
  <div className={`flex ${line.mine ? 'justify-end' : 'justify-start'}`}>
    <div
      className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
        line.mine
          ? 'rounded-br-md bg-primary-500 text-white'
          : 'rounded-bl-md bg-neutral-100 text-neutral-800'
      }`}
    >
      <p className={`text-[11px] font-semibold ${line.mine ? 'text-primary-50' : 'text-neutral-500'}`}>{line.name}</p>
      {/* Colour set here, not inherited: a global `p { color }` rule beats the bubble's
          text-white and left your own replies grey on orange. */}
      <p className={`mt-0.5 ${line.mine ? 'text-white' : 'text-neutral-800'}`}>{line.text}</p>
    </div>
  </div>
);

Bubble.propTypes = { line: PropTypes.object.isRequired };

const MicroSimPlayView = ({
  state,
  onChoose = () => {},
  onContinue = () => {},
  onViewReport = null,
  busy = false,
  error = null,
  topSlot = null,
}) => {
  const [selected, setSelected] = useState(null);
  const [, setTick] = useState(0);

  // A new turn (or a new round) clears any half-made selection.
  useEffect(() => { setSelected(null); }, [state.round, state.step, state.status]);

  // Re-render each second while the round-outcome countdown is visible.
  useEffect(() => {
    if (state.status !== 'round_end') return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [state.status]);

  const isObserver = state.viewer === 'observer';
  const roundLines = useMemo(
    () => (state.conversation || []).filter((l) => l.round === state.round),
    [state.conversation, state.round]
  );
  const turnsPlayedThisRound = roundLines.length;
  const iContinued = state.roundEnd?.continuedBy?.includes(state.me?.slot);
  const partnerContinued = state.roundEnd?.continuedBy?.includes(state.partner?.slot);
  const autoIn = secondsUntil(state.roundEnd?.autoAdvanceAt);

  return (
    <div className="mx-auto w-full max-w-xl px-4 pb-28 pt-4">
      {topSlot}

      {/* ---------- where am I ---------- */}
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-primary-600">
          Round {Math.min(state.round + 1, state.roundCount)} of {state.roundCount}
          {state.status === 'in_progress' && ` · Turn ${state.step + 1} of ${state.stepCount}`}
        </p>
        {state.topic?.industry && <span className="text-[11px] text-neutral-400">{state.topic.industry}</span>}
      </div>
      <h1 className="mt-1 text-xl font-bold text-neutral-900">{state.roundTitle}</h1>
      <div className="mt-2.5 flex gap-1" aria-hidden="true">
        {Array.from({ length: state.stepCount }, (_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full ${
              i < turnsPlayedThisRound || state.status !== 'in_progress' ? 'bg-primary-500' : 'bg-neutral-200'
            }`}
          />
        ))}
      </div>

      {/* ---------- who am I ---------- */}
      <div className="mt-4 flex items-center gap-2.5 rounded-xl bg-neutral-900 px-4 py-3">
        {isObserver ? <Eye size={15} className="text-neutral-400" /> : <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">You are</span>}
        <span className="text-sm font-bold text-white">
          {isObserver
            ? `Observing${state.me.number ? ` · you are #${state.me.number}` : ''}`
            : `${withNumber(state.me.name, state.me.number)} · ${state.me.roleLabel}`}
        </span>
        <span className="ml-auto truncate text-xs text-neutral-400">
          {isObserver
            ? (state.players || []).map((p) => withNumber(p.name, p.number)).join(' & ')
            : `with ${withNumber(state.partner.name, state.partner.number)} (${state.partner.roleLabel})`}
        </span>
      </div>

      {/* ---------- the situation ---------- */}
      <div className="mt-4 rounded-2xl border border-neutral-200 bg-white px-4 py-3.5">
        <p className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">The situation</p>
        <p className="mt-1 text-[15px] leading-relaxed text-neutral-800">{state.situation}</p>
      </div>

      {!isObserver && state.privateBrief && state.status !== 'completed' && (
        <div className="mt-3 rounded-2xl bg-primary-50 px-4 py-3.5 ring-1 ring-inset ring-primary-200">
          <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-primary-700">
            <Lock size={12} /> Your private brief — only you can see this
          </p>
          <p className="mt-1 text-sm leading-relaxed text-neutral-900">{state.privateBrief}</p>
        </div>
      )}

      {/* ---------- conversation so far ---------- */}
      <div className="mt-5">
        <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
          <MessageCircle size={12} /> Conversation so far
        </p>
        <div className="mt-2 space-y-2">
          {roundLines.length
            ? roundLines.map((line) => <Bubble key={`${line.round}-${line.step}`} line={line} />)
            : <p className="text-sm text-neutral-400">The first exchange starts here.</p>}
        </div>
      </div>

      {error && <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}

      {/* ---------- your move ---------- */}
      {state.status === 'in_progress' && state.turn?.isMine && (
        <div className="mt-6">
          <h2 className="text-base font-bold text-neutral-900">{state.turn.prompt}</h2>
          <p className="mt-0.5 text-sm text-neutral-500">Choose what you say next.</p>
          <div className="mt-3 space-y-2.5">
            {state.turn.options.map((option, i) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setSelected(option.id)}
                aria-pressed={selected === option.id}
                className={`w-full rounded-2xl border-2 p-3.5 text-left transition-colors ${
                  selected === option.id ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 bg-white hover:border-neutral-300'
                }`}
              >
                <span className="block text-[10px] font-bold uppercase tracking-wide text-primary-600">Option {i + 1}</span>
                <span className="mt-0.5 block text-[15px] leading-snug text-neutral-900">{option.text}</span>
              </button>
            ))}
          </div>
          <div className="sticky bottom-0 -mx-4 mt-4 border-t border-neutral-200 bg-white px-4 py-3">
            <button
              type="button"
              disabled={!selected || busy}
              onClick={() => onChoose(selected)}
              className="w-full rounded-xl bg-primary-500 py-3.5 text-base font-bold text-white transition-colors hover:bg-primary-600 disabled:bg-neutral-200 disabled:text-neutral-400"
            >
              {busy ? 'Sending…' : selected ? 'Send my reply' : 'Choose a reply to continue'}
            </button>
          </div>
        </div>
      )}

      {state.status === 'in_progress' && !state.turn?.isMine && (
        <div className="mt-6 flex items-center gap-3 rounded-2xl bg-neutral-50 px-4 py-4">
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-primary-500" />
          </span>
          <p className="text-sm text-neutral-700">
            <span className="font-semibold">{state.awaitingName || 'Your partner'}</span> is choosing a reply…
          </p>
        </div>
      )}

      {/* ---------- end of a round ---------- */}
      {state.status === 'round_end' && state.roundEnd && (
        <div className="mt-6 rounded-2xl border-2 border-primary-200 bg-white p-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-primary-600">Round {state.round + 1} complete</p>
          <h2 className="mt-1 text-lg font-bold text-neutral-900">{state.roundEnd.outcome?.title}</h2>
          <p className="mt-1 text-sm leading-relaxed text-neutral-700">{state.roundEnd.outcome?.body}</p>
          {state.round + 1 < state.roundCount && (
            <p className="mt-3 rounded-xl bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
              <span className="font-semibold">Next: swap roles.</span> In round {state.round + 2} the other person opens the conversation.
            </p>
          )}
          {!isObserver && (
            <button
              type="button"
              disabled={busy || iContinued}
              onClick={onContinue}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-3 text-base font-bold text-white hover:bg-primary-600 disabled:bg-neutral-200 disabled:text-neutral-500"
            >
              {iContinued ? <><Check size={16} /> Waiting for {state.partner.name}</> : state.round + 1 < state.roundCount ? 'Continue to round 2' : 'See my results'}
            </button>
          )}
          <p className="mt-2 text-center text-xs text-neutral-400">
            {partnerContinued && !iContinued && `${state.partner.name} is ready. `}
            {autoIn > 0 ? `Moving on automatically in ${autoIn}s` : 'Moving on…'}
          </p>
        </div>
      )}

      {/* ---------- finished ---------- */}
      {state.status === 'completed' && (
        <div className="mt-6 rounded-2xl bg-emerald-50 p-5 text-center ring-1 ring-inset ring-emerald-200">
          <p className="text-lg font-bold text-emerald-900">You’ve finished both rounds</p>
          {!isObserver && onViewReport && (
            <button
              type="button"
              onClick={onViewReport}
              className="mt-3 w-full rounded-xl bg-emerald-600 py-3 text-base font-bold text-white hover:bg-emerald-700"
            >
              See my results
            </button>
          )}
        </div>
      )}

      {state.status === 'abandoned' && (
        <div className="mt-6 rounded-2xl bg-neutral-100 p-5 text-center">
          <p className="font-semibold text-neutral-800">Your trainer has ended this micro simulation.</p>
        </div>
      )}
    </div>
  );
};

MicroSimPlayView.propTypes = {
  /** Server payload from forPlayer / forObserver. */
  state: PropTypes.object.isRequired,
  onChoose: PropTypes.func,
  onContinue: PropTypes.func,
  onViewReport: PropTypes.func,
  busy: PropTypes.bool,
  error: PropTypes.string,
  /** Extra content above the view (the trainer preview's seat switcher). */
  topSlot: PropTypes.node,
};

export default MicroSimPlayView;
